'use client';

import { useEffect } from 'react';

/**
 * Service Worker Registration Component
 * Registers the 3D asset caching service worker for instant back/forward navigation
 */
export default function ServiceWorkerRegistration() {
  useEffect(() => {
    // Only register in browser and if service workers are supported
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      console.log('[SW] Service workers not supported');
      return;
    }

    // Register the service worker
    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw-3d-cache.js', {
          scope: '/',
        });

        console.log('[SW] 3D Asset Cache service worker registered:', registration.scope);

        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                console.log('[SW] New service worker activated');
              }
            });
          }
        });

        // Check for updates every hour
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);

      } catch (error) {
        console.error('[SW] Service worker registration failed:', error);
      }
    };

    // Register after page load to not block main thread
    if (document.readyState === 'complete') {
      registerServiceWorker();
    } else {
      window.addEventListener('load', registerServiceWorker);
      return () => window.removeEventListener('load', registerServiceWorker);
    }
  }, []);

  // Helper function to check cache status (can be called from console)
  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      (window as any).get3DCacheSize = async () => {
        if (navigator.serviceWorker.controller) {
          const messageChannel = new MessageChannel();
          return new Promise((resolve) => {
            messageChannel.port1.onmessage = (event) => {
              console.log('[SW] Cache status:', event.data);
              resolve(event.data);
            };
            navigator.serviceWorker.controller.postMessage(
              { type: 'GET_CACHE_SIZE' },
              [messageChannel.port2]
            );
          });
        }
        return { error: 'No active service worker' };
      };

      (window as any).clear3DCache = async () => {
        if (navigator.serviceWorker.controller) {
          const messageChannel = new MessageChannel();
          return new Promise((resolve) => {
            messageChannel.port1.onmessage = (event) => {
              console.log('[SW] Cache cleared:', event.data);
              resolve(event.data);
            };
            navigator.serviceWorker.controller.postMessage(
              { type: 'CLEAR_3D_CACHE' },
              [messageChannel.port2]
            );
          });
        }
        return { error: 'No active service worker' };
      };

      console.log('[SW] Helper functions registered:');
      console.log('  - window.get3DCacheSize() - Check cache size');
      console.log('  - window.clear3DCache() - Clear 3D asset cache');
    }
  }, []);

  return null; // This component doesn't render anything
}

